import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ReferenceLine,
  ReferenceDot,
} from 'recharts';

import { BottomLineLabel } from '@/features/charts/components/chart-bottom-line';
import { BudgetLabel } from '@/features/charts/components/chart-budget-label';
import { BudgetHoverOverlay } from '@/features/charts/components/chart-budget-overlay';
import { RedSvgGradient } from '@/features/charts/components/chart-gradients';
import { MiddleLineLabel } from '@/features/charts/components/chart-middle-line';
import { DataPoint } from '@/features/charts/components/chart-types/line-chart-components/line-chart-data-point';
import { LineChartXAxisLabel } from '@/features/charts/components/chart-types/line-chart-components/line-chart-x-axis-label';
import { ChartWarningIcon } from '@/features/charts/components/chart-warning-icon';
import { useLineChartIntersection } from '@/features/charts/hooks/line-chart-intersection';
import { useBudgetOverlay } from '@/features/charts/hooks/use-budget-overlay';
import { formatChartValue } from '@/features/charts/util/chart-styling-util';
import {
  calculateUniqueTicksWithEqualSpacing,
  ChartResult,
  getKpiValueDataKey,
} from '@/features/charts/util/chart-util';
import { useKpiTranslations } from '@/features/projects/project-details/components/kpi-card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/shared/components/ui/chart';
import { ChartTooltipContentSingle } from '@/shared/components/ui/custom-chart-tooltip';
import { getKpiUnitAbbreviationTranslation } from '@/shared/hooks/use-kpi-unit-translation';
import { KpiUnit } from '@/shared/types/kpi';

export const CustomLineChart = ({
  chartResult,
  colors,
  className,
  maxValue,
  unit,
}: {
  chartResult: ChartResult;
  colors: string[];
  className?: string;
  maxValue?: number;
  unit: string | KpiUnit | null;
}) => {
  const { chartData, chartConfig } = chartResult;
  const kpiIds = React.useMemo(
    () => Object.keys(chartConfig).map(Number),
    [chartConfig],
  );

  const firstForecastIndex = chartData.findIndex((d) => d.isForecast);
  const hasForecasts = firstForecastIndex !== -1;
  const { tKpiTypes } = useKpiTranslations();

  const kpiLength = kpiIds.length;
  const unitAbbreviation = getKpiUnitAbbreviationTranslation(tKpiTypes, unit);

  const [tooltipActive, setTooltipActive] = React.useState(false);

  const lastDataPoint = chartData.findLast((d) => !d.isForecast);
  const currValue = lastDataPoint?.value ?? 0;

  const {
    isBudgetHovered: isHovered,
    mousePosition,
    handleBudgetMouseEnter: handleMouseEnter,
    handleBudgetMouseLeave: handleMouseLeave,
  } = useBudgetOverlay();

  const {
    findIntersectionPoint,
    findLastExceedingPoint,
    calculateMiddlePosition,
  } = useLineChartIntersection();

  const [isAnimationComplete, setIsAnimationComplete] = React.useState(false);

  const handleAnimationComplete = React.useCallback(() => {
    setIsAnimationComplete(true);
  }, []);

  const patchedChartData = React.useMemo(() => {
    return Object.keys(chartConfig)
      .map((kpiId) => {
        const kpiIdNum = Number(kpiId);
        const forecastDataKey = `${kpiId}_forecast`;
        const chartData = chartResult.chartData.filter(
          (d) => d.kpiId === kpiIdNum,
        );
        const historicalData = chartData.filter((d) => !d.isForecast);
        const forecastData = chartData.filter((d) => d.isForecast);
        const firstForecastDataPoint = forecastData[0];

        const historicalWithForecastKey = historicalData.map((d) => ({
          ...d,
          [forecastDataKey]: null,
        }));

        if (!firstForecastDataPoint) {
          return historicalWithForecastKey;
        }

        const forecastWithKey = forecastData.map((d) => ({
          ...d,
          [forecastDataKey]: d.forecastValue,
        }));

        return [
          ...historicalWithForecastKey,
          {
            ...firstForecastDataPoint,
            isForecast: false,
            value: firstForecastDataPoint?.forecastValue ?? null,
            [getKpiValueDataKey(kpiIdNum)]:
              firstForecastDataPoint?.forecastValue ?? null,
            [forecastDataKey]: null,
            forecastValue: null,
          },
          ...forecastWithKey,
        ].filter((d) => d !== undefined);
      })
      .flat();
  }, [chartResult.chartData]);

  const { uniqueTicks, tickLabels } = React.useMemo(
    () => calculateUniqueTicksWithEqualSpacing(patchedChartData),
    [patchedChartData],
  );

  return (
    <>
      <ChartContainer config={chartConfig} className={className}>
        <AreaChart
          accessibilityLayer
          data={patchedChartData}
          margin={{ top: 20, left: 20, right: 40 }}
        >
          {<CartesianGrid vertical={false} syncWithTicks />}
          <XAxis
            dataKey="date"
            domain={['auto', 'auto']}
            tickLine={false}
            tickMargin={16}
            type="number"
            scale="linear"
            ticks={uniqueTicks}
            interval={0}
            tick={({ x, y, payload }) => {
              const label = tickLabels.get(payload.value);

              return (
                <LineChartXAxisLabel
                  x={x}
                  y={y}
                  textAnchor="middle"
                  label={label}
                />
              );
            }}
          />
          <YAxis
            hide={true}
            domain={maxValue ? [0, maxValue] : undefined}
            ticks={[]}
            tickLine={false}
            axisLine={false}
            width={0}
          />
          <ChartTooltip
            cursor={false}
            active={tooltipActive}
            content={
              kpiLength === 1 ? (
                (props) => <ChartTooltipContentSingle {...props} />
              ) : (
                <ChartTooltipContent />
              )
            }
          />
          <defs>
            <RedSvgGradient />

            {/* Anomaly dot gradient */}
            <linearGradient
              id="orangeYellowDotGradient"
              x1="0"
              y1="0"
              x2="1"
              y2="0"
            >
              <stop offset="0%" stopColor="hsl(var(--custom-orange))" />
              <stop offset="100%" stopColor="hsl(var(--custom-yellow))" />
            </linearGradient>

            {/* Forecast blue gradient - vertical fade from top to bottom */}
            <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2370CA" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#2370CA" stopOpacity={0} />
            </linearGradient>

            {kpiIds.map((kpiId, index) => {
              const intersectionPoint = maxValue
                ? findIntersectionPoint({
                    data: chartData,
                    dataKey: getKpiValueDataKey(kpiId),
                    threshold: maxValue,
                  })
                : null;
              const hasIntersection = intersectionPoint !== null;

              const normalColor = colors[index];

              return (
                <React.Fragment key={`fillGradient${kpiId}`}>
                  <linearGradient
                    id={`fillGradient${kpiId}`}
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="0"
                  >
                    {hasIntersection ? (
                      <>
                        <stop
                          offset="0%"
                          stopColor={normalColor}
                          stopOpacity={0.2}
                        />
                        <stop
                          offset={`${intersectionPoint.percentage}%`}
                          stopColor={normalColor}
                          stopOpacity={0.5}
                        />
                        <stop
                          offset={`${intersectionPoint.percentage}%`}
                          stopColor="hsla(305, 91%, 45%, 0.7)"
                          stopOpacity={0.5}
                        />
                        <stop
                          offset="100%"
                          stopColor="hsla(359, 100%, 30%, 0.9)"
                          stopOpacity={0.7}
                        />
                      </>
                    ) : (
                      <>
                        <stop
                          offset="0%"
                          stopColor={normalColor}
                          stopOpacity={0.4}
                        />
                        <stop
                          offset="100%"
                          stopColor={normalColor}
                          stopOpacity={0.6}
                        />
                      </>
                    )}
                  </linearGradient>

                  <linearGradient
                    id={`strokeGradient`}
                    x1="0"
                    y1="0"
                    x2="1"
                    y2="0"
                  >
                    {hasIntersection ? (
                      <>
                        <stop offset="0%" stopColor={normalColor} />
                        <stop
                          offset={`${intersectionPoint.percentage}%`}
                          stopColor={normalColor}
                        />
                        <stop
                          offset={`${intersectionPoint.percentage}%`}
                          stopColor="hsla(305, 91%, 45%, 0.7)"
                        />
                        <stop
                          offset="100%"
                          stopColor="hsla(359, 100%, 30%, 0.9)"
                        />
                      </>
                    ) : (
                      <>
                        <stop offset="0%" stopColor={normalColor} />
                        <stop offset="100%" stopColor={normalColor} />
                      </>
                    )}
                  </linearGradient>
                </React.Fragment>
              );
            })}
          </defs>

          {kpiIds.map((kpiId, index) => {
            const intersectionPoint = maxValue
              ? findIntersectionPoint({
                  data: chartData,
                  dataKey: getKpiValueDataKey(kpiId),
                  threshold: maxValue,
                })
              : null;

            const lastExceedingPoint = maxValue
              ? findLastExceedingPoint({
                  data: chartData,
                  dataKey: getKpiValueDataKey(kpiId),
                  threshold: maxValue,
                })
              : null;

            const middlePosition =
              intersectionPoint && lastExceedingPoint
                ? calculateMiddlePosition(
                    intersectionPoint,
                    lastExceedingPoint,
                    chartData.length,
                  )
                : null;

            return (
              <React.Fragment key={`area-${kpiId}`}>
                <Area
                  key={`area-${kpiId}`}
                  onMouseEnter={() => setTooltipActive(true)}
                  onMouseLeave={() => setTooltipActive(false)}
                  onAnimationEnd={handleAnimationComplete}
                  data={patchedChartData}
                  dataKey={getKpiValueDataKey(kpiId)}
                  fill={`url(#fillGradient${kpiId})`}
                  stroke={`url(#strokeGradient)`}
                  strokeWidth={3}
                  fillOpacity={0.4}
                  type={'linear'}
                  activeDot={(props) => {
                    const { cx, cy, payload } = props;

                    if (!tooltipActive) {
                      return <div></div>;
                    }

                    return (
                      <DataPoint
                        cx={cx || 0}
                        cy={cy || 0}
                        colors={colors}
                        index={index}
                        r={4}
                        isAnomaly={payload.isAnomaly}
                      />
                    );
                  }}
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    return (
                      <DataPoint
                        cx={cx || 0}
                        cy={cy || 0}
                        colors={colors}
                        index={index}
                        isAnomaly={payload.isAnomaly}
                      />
                    );
                  }}
                />
                {middlePosition && (
                  <ReferenceDot
                    x={middlePosition.x}
                    y={middlePosition.y * 1.1}
                    ifOverflow="visible"
                    isFront={true}
                    shape={(props) => {
                      return (
                        <g
                          style={{
                            opacity: isAnimationComplete ? 1 : 0,
                            transition: 'opacity 1s ease-in',
                          }}
                        >
                          <ChartWarningIcon
                            x={props.cx}
                            y={props.cy}
                            maxValue={maxValue ?? 0}
                            currValue={lastExceedingPoint?.y || 0}
                            unit={unitAbbreviation}
                          />
                        </g>
                      );
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
          {hasForecasts && (
            <>
              {kpiIds.map((kpiId, index) => {
                const forecastDataKey = `${kpiId}_forecast`;

                return (
                  <Area
                    key={`forecast-${kpiId}`}
                    onMouseEnter={() => setTooltipActive(true)}
                    onMouseLeave={() => setTooltipActive(false)}
                    onAnimationEnd={handleAnimationComplete}
                    dataKey={forecastDataKey}
                    stroke="#2370CA"
                    strokeWidth={3}
                    strokeDasharray="12 12"
                    fill="url(#forecastGradient)"
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      const value = payload[forecastDataKey];
                      if (value === null || value === undefined) return <></>;
                      return (
                        <DataPoint
                          cx={cx || 0}
                          cy={cy || 0}
                          colors={colors}
                          index={index}
                          isAnomaly={payload.isAnomaly}
                        />
                      );
                    }}
                    activeDot={(props) => {
                      const { cx, cy, payload } = props;
                      const value = payload[forecastDataKey];
                      if (
                        !tooltipActive ||
                        value === null ||
                        value === undefined
                      )
                        return <></>;
                      return (
                        <DataPoint
                          cx={cx || 0}
                          cy={cy || 0}
                          colors={colors}
                          index={index}
                          isAnomaly={payload.isAnomaly}
                          r={4}
                        />
                      );
                    }}
                    connectNulls
                    type="linear"
                  />
                );
              })}
            </>
          )}
          {maxValue !== undefined && maxValue > 0 && (
            <>
              {/* Budget line at the top */}
              <ReferenceLine
                y={maxValue}
                stroke="transparent"
                label={({ viewBox }) => {
                  return (
                    <BudgetLabel
                      viewBox={viewBox}
                      maxValue={maxValue}
                      unit={unitAbbreviation}
                      onMouseEnter={() => {}}
                      onMouseLeave={() => {}}
                    />
                  );
                }}
              />
              <ReferenceLine
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                y={maxValue}
                strokeWidth={40}
                stroke="transparent"
              />
              {/* Middle reference line */}
              <ReferenceLine
                y={maxValue / 2}
                stroke="transparent"
                label={({ viewBox }) => {
                  return (
                    <MiddleLineLabel
                      viewBox={viewBox}
                      label={formatChartValue(maxValue / 2)}
                    />
                  );
                }}
              />
            </>
          )}
          {/* Bottom X-axis line (left-aligned) */}
          <ReferenceLine
            y={0}
            stroke="transparent"
            label={({ viewBox }) => {
              return <BottomLineLabel viewBox={viewBox} />;
            }}
          />
        </AreaChart>
      </ChartContainer>
      {maxValue !== undefined && maxValue > 0 && (
        <BudgetHoverOverlay
          key={`budget-hover-overlay-chart`}
          isVisible={isHovered}
          mouseX={mousePosition.x}
          mouseY={mousePosition.y}
          maxValue={maxValue}
          currValue={currValue}
          unit={unitAbbreviation}
        />
      )}
    </>
  );
};
